import { useEffect, useState } from "react";
import { Landmark, Plug } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading, Stat, Bar } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

export default function FinanceFunding() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/finance/overview").then(({ data }) => setData(data)); }, []);
  if (!data) return <Loading />;
  const { customers, applications, stats, connections } = data;

  return (
    <div className="animate-fade-up space-y-6" data-testid="finance-page">
      <div>
        <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-slate-900"><Landmark size={22} className="text-[#b0862f]" /> Finance & Funding</h1>
        <p className="mt-1 text-[13px] text-slate-500">Funding readiness across your customers and the applications your AI team is preparing. Live bank & lender data connect below.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Funding customers" value={stats.funding_customers} tone="slate" />
        <Stat label="Ready (80%+)" value={stats.ready} tone="green" />
        <Stat label="Applications" value={stats.applications} tone="blue" />
      </div>

      <Card className="p-5" data-testid="finance-connections">
        <div className="mb-3 flex items-center gap-2"><Plug size={15} className="text-amber-500" /><span className="font-head text-[14px] font-bold text-slate-900">Connections</span></div>
        <div className="grid gap-2 sm:grid-cols-2">
          {connections.map((c) => (
            <div key={c.name} className="flex items-center justify-between rounded-lg border border-dashed border-amber-300 bg-amber-50/40 px-3 py-2.5" data-testid="finance-connection">
              <div><div className="text-[13px] font-semibold text-slate-800">{c.name}</div><div className="font-mono text-[10px] text-slate-500">{c.purpose}</div></div>
              <button onClick={() => toast.message("Integration required", { description: `Connect ${c.name} — needs ${c.needs}.` })} data-testid="finance-setup-btn" className="shrink-0 rounded-lg bg-[#080d1a] px-3 py-1.5 text-[12px] font-bold text-white hover:bg-[#1a5fd0]">Set up</button>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-5" data-testid="finance-customers">
          <div className="mb-3 font-head text-[14px] font-bold text-slate-900">Funding readiness</div>
          {customers.length === 0 ? <div className="py-6 text-center text-[13px] text-slate-400">No customer profiles yet.</div> : (
            <div className="space-y-3">
              {customers.map((c, i) => (
                <div key={i}>
                  <div className="mb-1 flex items-center justify-between text-[13px]"><span className="font-semibold text-slate-800">{c.company}</span><span className="font-mono text-[11px] text-slate-500">{c.completeness != null ? `${c.completeness}%` : "—"}</span></div>
                  <Bar pct={c.completeness || 0} tone="#1a5fd0" />
                  {c.next_step && <div className="mt-0.5 text-[11px] text-slate-400">Next: {c.next_step}</div>}
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="p-5" data-testid="finance-applications">
          <div className="mb-3 font-head text-[14px] font-bold text-slate-900">Applications</div>
          {applications.length === 0 ? <div className="py-6 text-center text-[13px] text-slate-400">No applications prepared yet.</div> : (
            <div className="space-y-1.5">{applications.map((a, i) => (<div key={i} className="flex items-center justify-between text-[13px]"><span className="text-slate-700">{a.vendor}{a.customer ? <span className="text-slate-400"> · {a.customer}</span> : ""}</span><span className="font-mono text-[10px] text-slate-500">{a.status}</span></div>))}</div>
          )}
        </Card>
      </div>
    </div>
  );
}
